import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Coins, Loader2, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PageContainer, PageHeader, Card, Pill } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

export default function TipSettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const branchName = user?.default_branch?.name || "selected branch";
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["tip-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/tips/settings", {
        params: branchId ? { branch_id: branchId } : {},
      });
      return res.settings;
    },
    enabled: Boolean(user),
  });

  useEffect(() => {
    if (data && !form) setForm({ ...data });
  }, [data, form]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const { data: res } = await api.put("/tips/settings", { branch_id: branchId, ...payload });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message);
      setForm({ ...res.settings });
      queryClient.invalidateQueries({ queryKey: ["tip-settings", branchId] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not save."),
  });

  if (isLoading || !form) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading…
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={t("tips.title", "Tips & Service Charge")}
        subtitle={t("tips.subtitle", "Service charge rules and optional tips for {{branch}}.", { branch: branchName })}
      />

      <div className="mb-4 flex gap-2">
        <Pill tone={form.tips_enabled ? "green" : "slate"}>
          {form.tips_enabled ? "Tips enabled" : "Tips disabled"}
        </Pill>
        <Pill tone="blue">{form.service_charge_percent}% service charge (dine-in)</Pill>
      </div>

      <Card className="p-4 max-w-2xl space-y-5">
        <div>
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <Coins className="h-4 w-4" />
            Service charge rules
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            Auto-applied when a bill is created or order type changes. Card payments on POS are recorded manually — no online gateway.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <Label>Default service charge %</Label>
              <Input
                type="number"
                step="0.01"
                value={form.service_charge_percent}
                onChange={(e) => setForm({ ...form, service_charge_percent: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label>Apply to dine-in</Label>
              <Switch checked={form.apply_dine_in} onCheckedChange={(v) => setForm({ ...form, apply_dine_in: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Apply to takeaway</Label>
              <Switch checked={form.apply_takeaway} onCheckedChange={(v) => setForm({ ...form, apply_takeaway: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Apply to delivery</Label>
              <Switch checked={form.apply_delivery} onCheckedChange={(v) => setForm({ ...form, apply_delivery: v })} />
            </div>
            <p className="text-xs text-muted-foreground">Delivery is usually exempt; use parcel/delivery charge instead.</p>
          </div>
        </div>

        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label>Allow tips at payment</Label>
              <p className="text-sm text-muted-foreground">
                Cashier enters optional tip on POS (TIP mode). Hide the POS tip row in Administration → General Settings → Show tips on POS.
              </p>
            </div>
            <Switch checked={form.tips_enabled} onCheckedChange={(v) => setForm({ ...form, tips_enabled: v })} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Tip pool report</Label>
            <Switch checked={form.tip_pool_report} onCheckedChange={(v) => setForm({ ...form, tip_pool_report: v })} />
          </div>
        </div>

        <Button
          onClick={() =>
            saveMutation.mutate({
              ...form,
              service_charge_percent: Number(form.service_charge_percent) || 0,
            })
          }
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save settings
        </Button>
      </Card>
    </PageContainer>
  );
}
